"use client";

import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import {
  AddToCartButton,
  EcommerceProductsProvider,
  useEcommerceProducts,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

const staffNotes = [
  { snippet: "A gentle, loving soul", staffer: "Elena", rotation: "-1.5deg" },
  { snippet: "Perfectly plush companion", staffer: "Marcus", rotation: "1.2deg" },
  { snippet: "Rare and exquisite beauty", staffer: "Sofia", rotation: "-1deg" },
  { snippet: "Sweetest temperament ever", staffer: "Elena", rotation: "1.5deg" },
  { snippet: "A true feline treasure", staffer: "Marcus", rotation: "-0.8deg" },
  { snippet: "Pure elegance in motion", staffer: "Sofia", rotation: "1deg" },
];

const cardRotations = [
  "md:-rotate-[1.2deg]",
  "md:rotate-[0.8deg]",
  "md:-rotate-[0.5deg]",
  "md:rotate-[1deg]",
  "md:-rotate-[0.8deg]",
  "md:rotate-[1.2deg]",
];

function TreasuresConsumer() {
  const { products, loading, error } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            data-index={i}
            className="h-[420px] rounded-lg bg-card/60 animate-pulse shadow-md"
            style={{ borderColor: "var(--border)" }}
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-center" style={{ color: "var(--muted-foreground)" }}>
        We are currently updating our nursery. Please check back shortly.
      </p>
    );
  }

  if (products.length === 0) {
    return (
      <p className="text-center" style={{ color: "var(--muted-foreground)" }}>
        Our kittens are currently being pampered. New arrivals coming soon.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-12 lg:grid-cols-3">
      {products.slice(0, 6).map((product, i) => {
        const note = staffNotes[i % staffNotes.length];
        const rotation = cardRotations[i % cardRotations.length];
        const category = product.categories?.[0]?.name;

        return (
          <article
            key={product.id}
            data-index={i}
            className={`relative flex flex-col p-6 rounded-lg border bg-card transition-all duration-300 ease-out hover:rotate-0 hover:shadow-lg group ${rotation}`}
            style={{
              borderColor: "var(--border)",
              transitionTimingFunction: "cubic-bezier(.2,.7,.2,1)",
            }}
          >
            {}
            {product.price !== undefined && (
              <div
                className="absolute -top-3 -right-3 z-10 px-3 py-1.5 rounded-lg text-sm font-semibold shadow-md rotate-3"
                style={{
                  backgroundColor: "var(--primary)",
                  color: "var(--primary-foreground)",
                }}
              >
                ${product.price}
              </div>
            )}

            {}
            <Link to={product.link} className="block">
              <div
                className="overflow-hidden rounded-lg border mb-5"
                style={{ borderColor: "var(--border)" }}
              >
                <div className="aspect-[3/4] bg-muted overflow-hidden">
                  {product.featured_image_url ? (
                    <img
                      src={product.featured_image_url}
                      alt={product.title}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  ) : (
                    <div
                      className="h-full w-full flex items-center justify-center"
                      style={{
                        backgroundColor: "var(--muted)",
                        color: "var(--muted-foreground)",
                        fontFamily: "var(--font-serif)",
                      }}
                    >
                      <span className="text-4xl">{"\u201C"}</span>
                    </div>
                  )}
                </div>
              </div>
            </Link>

            {}
            {category && (
              <Badge
                variant="outline"
                className="mb-3 self-start"
                style={{
                  fontFamily: "var(--font-mono)",
                  color: "var(--secondary)",
                  borderColor: "var(--secondary)",
                  backgroundColor: "transparent",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  paddingLeft: "0.625rem",
                  paddingRight: "0.625rem",
                }}
              >
                {category}
              </Badge>
            )}

            {}
            <Link to={product.link}>
              <h3
                className="font-default text-xl md:text-2xl font-medium leading-tight mb-1.5 relative inline-block"
                style={{
                  fontFamily: "var(--font-serif)",
                  color: "var(--foreground)",
                  letterSpacing: "-0.012em",
                }}
              >
                <span className="relative">
                  {product.title}
                  <span
                    aria-hidden="true"
                    className="absolute left-0 right-0 -bottom-1 h-[2px] origin-left scale-x-0 transition-transform duration-[320ms] ease-out group-hover:scale-x-100"
                    style={{ backgroundColor: "var(--primary)" }}
                  />
                </span>
              </h3>
            </Link>

            {}
            {product.short_description && (
              <div
                className="text-sm mb-5 [&_p]:mb-0 [&_p:last-child]:mb-0 line-clamp-2"
                style={{ color: "var(--muted-foreground)" }}
                dangerouslySetInnerHTML={{ __html: product.short_description }}
              />
            )}

            {}
            <div
              className="mb-5 pl-3 border-l-2"
              style={{
                borderColor: "var(--primary)",
                transform: `rotate(${note.rotation})`,
              }}
            >
              <p
                className="leading-snug"
                style={{
                  fontFamily: "var(--font-mono)",
                  color: "var(--muted-foreground)",
                  fontSize: "1.125rem",
                }}
              >
                {note.snippet} — {note.staffer}
              </p>
            </div>

            {}
            <div className="mt-auto w-full">
              <AddToCartButton
                productId={product.id}
                variant="default"
                className="w-full"
                buttonClassName="bg-primary text-primary-foreground rounded-lg"
                text="Inquire Now"
                showQuantity={false}
              />
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default function AvailableKittens() {
  const wp_query_params = { per_page: 6, orderby: "date", order: "desc" };
  const wp_query = new WP_Query(wp_query_params);

  return (
    <section data-section-id="12012"
      id="availablekittensgallery"
      className="relative py-24 md:py-32 bg-background text-foreground"
    >
      {}
      <div className="absolute top-0 left-0 right-0 flex justify-center px-6 md:px-24 pointer-events-none">
        <svg
          viewBox="0 0 1200 24"
          preserveAspectRatio="none"
          className="w-full max-w-6xl h-6"
          aria-hidden="true"
        >
          <path
            d="M0,12 C100,4 200,20 300,12 C400,4 500,20 600,12 C700,4 800,20 900,12 C1000,4 1100,20 1200,12"
            fill="none"
            stroke="var(--border)"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-10">
        {}
        <div className="mx-auto max-w-[720px] text-center mb-16 md:mb-20">
          <p
            className="font-default uppercase mb-5"
            style={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              letterSpacing: "0.14em",
              color: "var(--muted-foreground)",
            }}
          >
            {"Available companions"}
          </p>

          <h2
            className="font-default mb-6"
            style={{
              fontFamily: "var(--font-serif)",
              fontSize: "clamp(2rem, 3.6vw, 3.5rem)",
              fontWeight: 500,
              lineHeight: 1.08,
              letterSpacing: "-0.018em",
              color: "var(--foreground)",
            }}
          >
            {"Meet our "}
            <span className="relative inline-block">
              <span className="relative z-10">precious</span>
              <svg
                viewBox="0 0 200 28"
                preserveAspectRatio="none"
                className="absolute left-0 right-0 -bottom-1 w-full h-3 md:h-4"
                aria-hidden="true"
              >
                <path
                  d="M4,18 C40,8 90,22 130,14 C160,8 185,16 196,12"
                  fill="none"
                  stroke="var(--primary)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            {" kittens waiting for their forever homes."}
          </h2>

          <p
            className="font-default"
            style={{
              fontSize: "clamp(1.0625rem, 1.05vw, 1.1875rem)",
              lineHeight: 1.6,
              color: "var(--muted-foreground)",
            }}
          >
            Hand-raised with love in Tampa, our exotic kittens are ready to bring warmth and joy to your family.
          </p>
        </div>

        {}
        <EcommerceProductsProvider wp_query={{ per_page: 6, orderby: "date", order: "desc" }}>
          <TreasuresConsumer />
        </EcommerceProductsProvider>

        {}
        <div className="mt-16 md:mt-20 flex justify-center">
          <Link
            to="/ShopPage"
            className="group inline-flex items-center gap-2 font-default text-base font-medium"
            style={{ color: "var(--foreground)" }}
          >
            <span className="relative">
              View all available kittens
              <span
                aria-hidden="true"
                className="absolute left-0 right-0 -bottom-1 h-[1.5px] transition-all duration-200 group-hover:h-[2.5px]"
                style={{ backgroundColor: "var(--primary)" }}
              />
            </span>
            <ArrowRight
              className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1"
              style={{ color: "var(--primary)" }}
            />
          </Link>
        </div>
      </div>
    </section>
  );
}